/**
 * The Fit Score band chip — one component, so a Fit Score looks the same everywhere.
 *
 * Extracted from the Solutions dashboard when the Suggested Resource picker (RP-18.2)
 * started scoring candidates. A candidate's fit and a Solution's fit are the same bands
 * on the same scale, and two hand-rolled chips would have drifted apart the first time
 * one of them was restyled.
 *
 * Color is the `--fit-*` product tokens — the band IS the meaning, so it never takes a
 * theme color.
 */

import type { FitTier } from "@/lib/solution-filters";
import { cn } from "@/lib/utils";

const TONE: Record<FitTier, string> = {
  Strong: "bg-[var(--fit-strong-bg)] text-[var(--fit-strong)]",
  Moderate: "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]",
  "At Risk": "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]",
};

export function FitBadge(props: {
  /**
   * `partial` marks a score computed from fewer dimensions than the full composite — it
   * renders a `~` so two chips on one screen are never silently read as one scale.
   */
  fit: { tier: FitTier; score: string | number; partial?: boolean } | null;
  /** `xs` for inside a grid row or a picker result; `sm` for a table cell. */
  size?: "xs" | "sm";
  /** What the score means here — the chip is a number until this says otherwise. */
  title?: string;
  /** Rendered when there is no score. A dash reads as "none" where a blank reads as a bug. */
  fallback?: React.ReactNode;
  /** Drop the tier word and keep the dot + score, for a narrow cell. The color is the band. */
  hideTier?: boolean;
}) {
  const size = props.size ?? "sm";
  if (props.fit === null) {
    return (
      props.fallback ?? (
        <span className={cn("text-muted-foreground", size === "xs" ? "text-xs" : "text-sm")}>
          —
        </span>
      )
    );
  }
  return (
    <span
      title={props.title}
      className={cn(
        "inline-flex items-center gap-1 rounded font-semibold whitespace-nowrap",
        size === "xs" ? "px-1 py-0 text-xs" : "px-1.5 py-0.5 text-sm",
        props.title !== undefined && "cursor-help",
        TONE[props.fit.tier],
      )}
    >
      <span className="leading-none">●</span>
      {props.hideTier !== true && <span>{props.fit.tier}</span>}
      <span className="opacity-70 tabular-nums">
        {props.fit.partial === true ? "~" : ""}
        {props.fit.score}%
      </span>
    </span>
  );
}
